import { lazy, ComponentType } from 'react'

type LazyFactory = () => Promise<{ default: ComponentType<any> }>

// Retry only helps transient network blips. For stale-deployment 404s all
// retries will fail and ErrorBoundary's auto-reload is the real fix, so
// keep the count low to minimize wasted latency.
const MAX_RETRIES = 2
const RETRY_DELAY_MS = 500

function wait(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}

async function importWithRetry(
  factory: LazyFactory,
  retries = MAX_RETRIES
): Promise<{ default: ComponentType<any> }> {
  try {
    return await factory()
  } catch (error) {
    if (retries <= 0) throw error
    await wait(RETRY_DELAY_MS)
    return importWithRetry(factory, retries - 1)
  }
}

/**
 * Drop-in replacement for React.lazy that automatically retries failed
 * dynamic imports (e.g. after a transient network blip). For permanent
 * failures (stale chunk 404 after deployment), retries exhaust quickly
 * and ErrorBoundary takes over with a full page reload.
 */
export default function lazyWithRetry(factory: LazyFactory) {
  return lazy(() => importWithRetry(factory))
}
